// Third-party
import React from 'react';
import { connect } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

// Redux
import { ReduxState } from '../../../../redux/modules/types';
import { AnnouncementParticipant } from '../../../../redux/modules/types/announcements';
import { getAnnouncementParticipants } from '../../../../redux/modules/announcements/admin';

// Utils
import { formatMessage, FormattedMessage } from '../../../../utils/i18n';
import { getColorFromString } from '../../../../utils/colors';
import { getContactInitials } from '../../../../utils/contacts';

// Components
import Avatar from '../../avatar/Avatar';
import { SearchInput } from '../../inputs';
import { LoadingSpinner } from '../../common';
import { Tooltip } from '../../tooltips';
import DataTable from '../../data/DataTable';
import { Pill } from '@capintel/component-lib/pill';

type DispatchProps = {
  getAnnouncementParticipants: (announcementId: string, query?: string, nextToken?: string) => Promise<any[]>;
};

type StateProps = {
  participants: any[];
  nextParticipantsToken?: string;
};

type Props = {
  announcementId: string;
  className: string;
};

type State = {
  searching: boolean;
  searchQuery: string;
};

class AnnouncementParticipants extends React.Component<Props & StateProps & DispatchProps, State> {
  readonly state: State = {
    searching: false,
    searchQuery: '',
  };

  onSearchParticipants = debounce((searchQuery: string) => {
    this.setState({ searchQuery });

    if (searchQuery) {
      const { announcementId } = this.props;

      this.setState({ searching: true });
      this.props.getAnnouncementParticipants(announcementId, searchQuery, undefined).finally(() => {
        this.setState({ searching: false });
      });
    }
  }, 250);

  onLoadMore = () => {
    const { announcementId, nextParticipantsToken } = this.props;
    const { searchQuery } = this.state;

    this.props.getAnnouncementParticipants(announcementId, searchQuery, nextParticipantsToken);
  };

  renderResultTable = () => {
    const { nextParticipantsToken, participants } = this.props;
    const { searching } = this.state;

    if (searching) {
      return (
        <p className="base-text no-query d-flex flex-grow-1 flex-shrink-1">
          <LoadingSpinner />
        </p>
      );
    }

    return (
      <div className="announcement-participants-table d-flex flex-column">
        <div className="participants-scrollbar">
          <DataTable
            columns={this.getTableColumns()}
            data={participants}
            noDataText={formatMessage('AnnouncementsAdmin.AnnouncementParticipantsTable.NoRows')}
            showLoadMore={!!nextParticipantsToken}
          />
          {!!nextParticipantsToken && !searching && <Waypoint onEnter={this.onLoadMore} />}
        </div>
      </div>
    );
  };

  getTableColumns = () => [
    {
      key: 'name',
      className: 'name',
      width: 340,
      Header: () => (
        <span>
          <FormattedMessage id="AnnouncementsAdmin.AnnouncementParticipantsTable.Headers.Name" />
        </span>
      ),
      Cell: ({ original }: { original: AnnouncementParticipant }) => {
        const { firstName, lastName, email } = original;
        const bgColor = getColorFromString(email, 'light');
        const initials = getContactInitials({ fname: firstName, lname: lastName, email });

        return (
          <>
            <Avatar backgroundColor={bgColor}>{initials}</Avatar>
            {`${firstName || ''} ${lastName || ''}`}
          </>
        );
      },
    },
    {
      key: 'email',
      accessor: 'email',
      className: 'email',
      Header: () => (
        <span>
          <FormattedMessage id="AnnouncementsAdmin.AnnouncementParticipantsTable.Headers.Email" />
        </span>
      ),
      Cell: ({ value }: { value: string }) => (
        <span className="custom-tooltip" title={value}>
          {value}
        </span>
      ),
    },
    {
      key: 'acknowledged',
      accessor: 'acknowledged',
      className: 'acknowledged',
      headerClassName: 'acknowledged',
      width: 100,
      Header: () => (
        <span>
          <FormattedMessage id="AnnouncementsAdmin.AnnouncementParticipantsTable.Headers.Acknowledged" />
          <Tooltip message={formatMessage('AnnouncementsAdmin.AnnouncementParticipantsTable.Headers.AcknowledgedTooltip')}>
            <i className="icon-exclamation" />
          </Tooltip>
        </span>
      ),
      Cell: ({ value }: { value: boolean }) => (
        <Pill variant={value ? 'success' : 'default'}>
          {formatMessage(`AnnouncementsAdmin.AnnouncementParticipantsTable.Data.Acknowledged.${value.toString()}`)}
        </Pill>
      ),
    },
  ];

  render() {
    const { className } = this.props;
    const { searchQuery } = this.state;

    return (
      <div className={classNames('announcement-participants d-flex flex-column flex-grow-1 flex-shrink-1', className)}>
        <SearchInput
          className="w-100"
          onChange={this.onSearchParticipants}
          placeholder={formatMessage('AnnouncementsAdmin.Details.Participants.SearchPlaceholder')}
        />
        <div className="d-flex flex-column flex-grow-1 flex-shrink-1">
          {searchQuery ? (
            this.renderResultTable()
          ) : (
            <p className="base-text no-query d-flex flex-grow-1 flex-shrink-1">
              <FormattedMessage id="AnnouncementsAdmin.Details.Participants.NoQuery" />
            </p>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  participants: state.announcementsAdmin.participants,
  nextParticipantsToken: state.announcementsAdmin.nextParticipantsToken,
});

export default connect(mapStateToProps, {
  getAnnouncementParticipants,
})(AnnouncementParticipants);
